/* TruthReceipt · Constitutional Refactor · Batch C2b
 * ====================================================
 * The single primitive that displays the outcome of an async action.
 *
 * The Truth Doctrine states: "nothing silently disappears".
 * Every async surface renders this component whenever an action
 * begins, updates, or completes. It is a docked receipt — never a
 * toast that fades, never a bare spinner, never a silent redirect.
 *
 * States (from useTruthState):
 *   saved · processing · waiting · needs_input · failed · completed
 *
 * When state is null → the receipt renders nothing (idle).
 */
import { CANONICAL_TRUTH_STATES } from "../hooks/useTruthState";
import { CheckCircle2, Loader2, Hourglass, HandMetal, AlertCircle, Sparkles } from "lucide-react";

const STATE_META = {
  saved:       { label: "Saved",       tone: "text-slate-700 dark:text-slate-200",  bg: "bg-slate-100 dark:bg-slate-800",   Icon: CheckCircle2 },
  processing:  { label: "Processing",  tone: "text-sky-700 dark:text-sky-200",      bg: "bg-sky-50 dark:bg-sky-900/30",     Icon: Loader2      },
  waiting:     { label: "Waiting",     tone: "text-amber-700 dark:text-amber-200",  bg: "bg-amber-50 dark:bg-amber-900/30", Icon: Hourglass    },
  needs_input: { label: "Needs input", tone: "text-violet-700 dark:text-violet-200",bg: "bg-violet-50 dark:bg-violet-900/30",Icon: HandMetal   },
  failed:      { label: "Failed",      tone: "text-rose-700 dark:text-rose-200",    bg: "bg-rose-50 dark:bg-rose-900/30",   Icon: AlertCircle  },
  completed:   { label: "Completed",   tone: "text-emerald-700 dark:text-emerald-200", bg: "bg-emerald-50 dark:bg-emerald-900/30", Icon: Sparkles },
};

export function TruthReceipt({ state, message = null, onRetry = null, onDismiss = null }) {
  if (!state) return null;
  if (!CANONICAL_TRUTH_STATES.includes(state)) {
    // Truth Doctrine: an invalid state IS a bug, and must be
    // visibly reported to the user — never silenced.
    return (
      <div
        data-testid="truth-receipt"
        data-truth-state="invalid"
        className="mt-2 rounded-md border border-rose-300 bg-rose-50 text-rose-800 text-xs px-2 py-1.5"
      >
        Invalid truth state · {String(state)}
      </div>
    );
  }
  const meta = STATE_META[state];
  const spin = state === "processing" ? "animate-spin" : "";
  return (
    <div
      data-testid="truth-receipt"
      data-truth-state={state}
      className={`mt-2 rounded-md border border-slate-200 dark:border-slate-700 ${meta.bg} px-2 py-1.5 flex items-center gap-2`}
    >
      <meta.Icon className={`w-3.5 h-3.5 ${meta.tone} ${spin}`} />
      <span className={`text-[11px] font-semibold uppercase tracking-wide ${meta.tone}`}>
        {meta.label}
      </span>
      {message && (
        <span data-testid={`truth-receipt-message`} className="text-[11px] text-slate-600 dark:text-slate-300 truncate">
          {message}
        </span>
      )}
      <span className="flex-1" />
      {state === "failed" && typeof onRetry === "function" && (
        <button
          type="button"
          data-testid="truth-receipt-retry"
          onClick={onRetry}
          className="text-[11px] font-semibold text-rose-700 dark:text-rose-200 underline"
        >
          Retry
        </button>
      )}
      {(state === "completed" || state === "failed") && typeof onDismiss === "function" && (
        <button
          type="button"
          data-testid="truth-receipt-dismiss"
          onClick={onDismiss}
          className="text-[11px] text-slate-500 dark:text-slate-400 hover:text-slate-800"
        >
          Dismiss
        </button>
      )}
    </div>
  );
}

export default TruthReceipt;
